import AutoTopupCheckoutOptIn from '@/components/Credits/AutoTopupCheckoutOptIn';
import { autoTopupSettingsForPack, canOfferAutoTopupForPack } from '@/lib/autoTopupOffer';
import { useCreditPacksStore } from '@/store/creditPacks';
import { useToastStore } from '@/store/toast';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import { API_HOST } from '@/constants/config';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import useIosIap, { getCreditPackIapProductId } from '@/hooks/useIosIap';
import { nativeCheckoutResult, nativeSubscriptionPlans } from '@/lib/nativePayment';
import { resumeBtcPayCheckouts } from '@/lib/btcpayCheckout';
import { usersApi, whopApi } from '@/lib/api';
import { loadWhopElements, openWhopReturnPage, whopCheckoutAppearance, saveWhopCheckoutOptIn } from '@/lib/whopElements';
import { accountApi } from '@/lib/api/account';
import {
  CHECKOUT_ABANDON_REASON,
  createCheckoutAttemptTracker,
  nativeIapOutcome,
} from '@/lib/checkoutTracking';
import { trackCheckoutFunnelEvent, trackPurchaseCompleted } from '@/constants/utils/analyticsEvents';
import { checkValmarAvailable, clearValmarAvailabilityCache } from '@/constants/utils/valmarAvailability';
import {
  describeWhopPaymentError,
  isValmarBlockedError,
  readPreferInternationalCheckout,
  resolveIsInternationalFromServer,
  resolveWebCheckoutProvider,
  shouldPrefetchValmarAvailability,
  writePreferInternationalCheckout,
} from '@/constants/utils/webCheckoutRouting';
import { hasValidPlusAccess, shouldForceWhopCheckout, PROCESSOR } from '@/lib/purchaseUtils';
import { trackWhopPurchase } from '@/constants/utils/analytics';

const GabPurchaseContext = createContext(null);
const HELD_CHECKOUT_MESSAGE = 'Paid checkout is unavailable while this account is under review.';
// How often an open Whop form asks whether its order has been fulfilled, and
// the checks made after the form goes away for a payment that settled as it did.
const WHOP_STATUS_POLL_MS = 4000;
const WHOP_STATUS_AFTER_CLOSE_MS = [3000, 10000];

function assertClientCanStartPaidCheckout(heldForReview) {
  if (!heldForReview) return;
  try {
    useModalStore.getState().actions.openModal('AccountReviewHoldModal', { source: 'checkout' });
  } catch (_) { /* best effort */ }
  const error = new Error(HELD_CHECKOUT_MESSAGE);
  error.code = 'paid_checkout_blocked';
  error.reason = 'held_for_review';
  throw error;
}

/**
 * Platform-specific purchase handling:
 * - Web: Uses Valmar (US) or Whop (international / known Whop subscriber / Valmar blocked)
 * - iOS: Uses Apple In-App Purchase (IAP) exclusively for all subscriptions and credit packs.
 *   No external payment (Valmar/Whop) fallback — required by App Store guidelines.
 * - Android: Uses Google Play In-App Purchase exclusively for all subscriptions and credit packs.
 *   No external payment (Valmar/Whop) fallback — mirrors the iOS IAP-only policy.
 */
export const isNativePlatform = Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos';

// Which processor a native store purchase runs through, for the checkout funnel.
function nativeStoreProcessor() {
  return Platform.OS === 'android' ? 'google' : 'apple';
}



export function GabPurchaseProvider({ children }) {
  const { theme, colors } = useTheme();
  const userId = useAuthStore(state => state.user?.id);
  const userEmail = useAuthStore(state => state.user?.email);
  const userIsPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const userIsComped = useAuthStore(state => state.user?.flags?.isComped);
  const userHeldForReview = useAuthStore(state => state.user?.flags?.heldForReview === true);
  const plusExpiresAt = useAuthStore(state => state.user?.plusExpiresAt);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const hasWhopSubscription = useAuthStore(state => state.user?.whop?.hasSubscription);
  const isWhopSubscriber = shouldForceWhopCheckout(activePurchase, Boolean(hasWhopSubscription));
  const userHasValidPlus = hasValidPlusAccess({
    isPlus: userIsPlus,
    isComped: userIsComped,
    plusExpiresAt,
    purchase: activePurchase,
  });
  // This provider is mounted at the app root, so its effects run on every public
  // marketing page too. Anonymous visitors cannot reach checkout from any of them —
  // PricingPage, the pricing/credits modals and settings all route to /signup first —
  // so the two preflights below are pure speculation for them. Derived the same way
  // PricingPage derives it, since that is the gate that actually governs the access.
  const authToken = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = Boolean(authToken) && !isTokenExpired();
  useEffect(() => {
    if (!isAuthenticated || !userId || Platform.OS !== 'web') return;
    return resumeBtcPayCheckouts(userId);
  }, [isAuthenticated, userId]);
  const { isXS } = useResponsiveness();
  const iap = useIosIap();
  const [isOpen, setIsOpen] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const pendingPromiseRef = useRef({ resolve: null, reject: null, onEvent: null });
  const isDarkTheme = theme === 'muted' || theme === 'sepia' || theme === 'night' || theme === 'smoke' || theme === 'black';

  // Whop checkout state (international, known Whop subscriber, or Valmar blocked)
  const [whopCheckout, setWhopCheckout] = useState(null); // { planId, configurationId, type, fallbackFromValmar? }
  // Elements owns declines and 3DS retries; this state handles load failures.
  const [whopPaymentError, setWhopPaymentError] = useState(null);
  const [whopAutoOffer, setWhopAutoOffer] = useState(null);
  const [whopAutoChecked, setWhopAutoChecked] = useState(false);
  const whopContainerRef = useRef(null);
  const whopElementRef = useRef(null);
  const whopAppearanceRef = useRef(null);
  whopAppearanceRef.current = whopCheckoutAppearance(isDarkTheme, colors);
  const [whopLoading, setWhopLoading] = useState(false);
  const [whopRetrying, setWhopRetrying] = useState(false);
  const whopRequestGeneration = useRef(0);
  const checkoutIframeRef = useRef(null);
  const pendingValmarCheckoutRef = useRef(null);
  const pendingValmarUpdateRef = useRef(null);
  const openWhopPlusCheckoutRef = useRef(null);
  const openWhopCreditsCheckoutRef = useRef(null);
  // One live checkout attempt at a time; every start resolves to exactly one of
  // completed / failed / abandoned so the funnel adds up (R-aug-02).
  const checkoutTrackerRef = useRef(null);
  if (!checkoutTrackerRef.current) {
    // One emit, two destinations: the backend keeps the per-user funnel history
    // for cohort work, Matomo puts the same step next to the campaign, landing
    // page and device that produced it. Sharing the emit is what keeps the two
    // from drifting into disagreeing funnels.
    checkoutTrackerRef.current = createCheckoutAttemptTracker((payload) => {
      usersApi.trackCheckoutEvent(payload);
      trackCheckoutFunnelEvent(payload);
    });
  }
  const checkoutTracker = checkoutTrackerRef.current;

  // Purchase completion analytics require confirmed fulfillment, not just a store charge.
  const trackNativeIapOutcome = useCallback((result) => {
    // `complete()` clears the attempt, so the context Matomo needs has to be
    // read off it first.
    const attempt = checkoutTracker.peek();
    switch (nativeIapOutcome(result)) {
      case 'completed':
        checkoutTracker.complete();
        trackPurchaseCompleted({
          checkoutType: attempt?.checkoutType,
          processor: attempt?.processor,
        });
        break;
      case 'pending':
        break;
      case 'abandoned':
        checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
        break;
      default:
        checkoutTracker.fail(result?.error);
    }
  }, [checkoutTracker]);

  // Check if user is international (non-US) - they use Whop checkout
  // Uses IP geolocation for accurate detection at checkout time
  const [isInternational, setIsInternational] = useState(false);
  const [ipCheckDone, setIpCheckDone] = useState(false);
  const checkoutSource = Platform.OS === 'web' ? 'web' : Platform.OS;

  const whopReturnUrl = useMemo(() => {
    if (Platform.OS !== 'web' || !whopCheckout || typeof window === 'undefined') return undefined;
    const url = new URL('/payment-successful', window.location.origin);
    // Match the server-authored redirect: a card swap returns as update-payment.
    url.searchParams.set('checkoutType', whopCheckout.type === 'credits' ? 'credits'
      : whopCheckout.purpose === 'update-payment' ? 'update-payment' : 'plus');
    url.searchParams.set('checkoutContext', whopCheckout.checkoutContextId);
    return url.href;
  }, [whopCheckout]);

  // Country resolved by our own backend from the request (Cloudflare edge, then
  // a local IP database). Free, unmetered and more trustworthy than the
  // third-party lookup, which has a small daily quota.
  const serverCountryCode = useAuthStore(state => state.user?.countryCode);
  const serverIsInternational = useAuthStore(state => state.user?.isInternational);
  const serverResolvedInternational = resolveIsInternationalFromServer({
    countryCode: serverCountryCode,
    isInternational: serverIsInternational,
  });

  // Resolve geo as soon as possible, preferring our own server's answer and
  // only paying for the third-party lookup when it told us nothing (anonymous
  // visitors, or before the user payload lands).
  useEffect(() => {
    // Nothing consumes `isInternational` while logged out (every reader is a settings
    // or subscription surface), and `ensureIpCheck()` resolves it on demand at checkout
    // anyway, so resolving it eagerly for an anonymous visitor buys nothing.
    if (!isAuthenticated) return undefined;

    if (serverResolvedInternational !== null) {
      setIsInternational(serverResolvedInternational);
      setIpCheckDone(true);
      return undefined;
    }

    if (ipCheckDone) return undefined;

    let cancelled = false;
    whopApi.checkIsInternational()
      .then((result) => {
        if (cancelled) return;
        console.log('[GabPurchase] IP geolocation result:', result);
        setIsInternational(result);
      })
      .catch((error) => {
        if (cancelled) return;
        // Whop clears cards worldwide; Valmar only clears US cards.
        console.warn('[GabPurchase] IP check failed, assuming international:', error);
        setIsInternational(true);
      })
      .finally(() => {
        if (!cancelled) setIpCheckDone(true);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated, serverResolvedInternational, ipCheckDone]);

  // Prefetch Valmar availability for signed-in US web users so checkout routing is
  // ready sooner. See shouldPrefetchValmarAvailability for why the auth gate is load
  // bearing — the probe opens live payment iframes, and this provider is mounted at the
  // app root, so it would otherwise run on every marketing page.
  useEffect(() => {
    if (!shouldPrefetchValmarAvailability({
      platform: Platform.OS,
      isAuthenticated,
      ipCheckDone,
      isInternational,
      isWhopSubscriber,
    })) return;

    checkValmarAvailable().catch((error) => {
      console.warn('[GabPurchase] Valmar availability preflight failed:', error);
    });
  }, [isAuthenticated, ipCheckDone, isInternational, isWhopSubscriber]);

  // Helper to ensure geo is resolved before checkout
  const ensureIpCheck = useCallback(async () => {
    if (serverResolvedInternational !== null) return serverResolvedInternational;
    if (ipCheckDone) return isInternational;
    
    try {
      const result = await whopApi.checkIsInternational();
      console.log('[GabPurchase] IP geolocation result (on-demand):', result);
      setIsInternational(result);
      setIpCheckDone(true);
      return result;
    } catch (error) {
      // Sending a foreign buyer to Valmar guarantees failure, so an unresolved
      // country routes to Whop instead.
      console.warn('[GabPurchase] IP check failed, assuming international:', error);
      setIsInternational(true);
      setIpCheckDone(true);
      return true;
    }
  }, [ipCheckDone, isInternational, serverResolvedInternational]);

  const resolveCheckoutProvider = useCallback(async () => {
    const userIsInternational = await ensureIpCheck();
    const preferInternational = readPreferInternationalCheckout();
    const skipValmarProbe = userIsInternational || isWhopSubscriber || preferInternational;
    const valmarAvailable = skipValmarProbe ? true : await checkValmarAvailable();
    const provider = resolveWebCheckoutProvider({
      isInternational: userIsInternational,
      valmarAvailable,
      isWhopSubscriber,
      preferInternational,
    });
    console.log('[GabPurchase] Checkout provider resolved:', {
      isInternational: userIsInternational,
      isWhopSubscriber,
      preferInternational,
      valmarAvailable,
      provider,
    });
    return { provider, userIsInternational, valmarAvailable };
  }, [ensureIpCheck, isWhopSubscriber]);

  const beginPendingCheckoutPromise = useCallback((options = {}) => new Promise((resolve, reject) => {
    pendingPromiseRef.current = {
      resolve,
      reject,
      onEvent: typeof options.onEvent === 'function' ? options.onEvent : null,
    };
  }), []);

  const launchWhopCheckoutUi = useCallback((response, options = {}, { fallbackFromValmar = false, type = 'plus', purpose = null } = {}) => {
    if (fallbackFromValmar) {
      writePreferInternationalCheckout();
    }
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    setCheckoutUrl(null);
    setWhopPaymentError(null);
    const promoHint = typeof options.promoCode === 'string'
      ? options.promoCode.trim().toUpperCase()
      : '';
    const offerUser = useAuthStore.getState().user;
    const offerPack = useCreditPacksStore.getState().creditPacks.find(pack => pack.id === options.creditPackId);
    setWhopAutoChecked(Boolean(options.autoTopupAuthorization));
    setWhopAutoOffer(type === 'credits' && !options.autoTopupHandled && canOfferAutoTopupForPack({ user: offerUser, usesWhopCheckout: true, platform: Platform.OS, pack: offerPack })
      ? { pack: offerPack, settings: autoTopupSettingsForPack(offerPack, { version: offerUser.autoTopup.version, provider: 'whop', paymentMethodFingerprint: offerUser.autoTopup.paymentMethodFingerprint }) } : null);
    const remembered = saveWhopCheckoutOptIn({
      contextId: response.checkoutContextId, userId: offerUser?.id,
      authorization: options.autoTopupAuthorization?.provider === 'whop' ? options.autoTopupAuthorization : null,
      returnPath: typeof window === 'undefined' ? '/app' : window.location.pathname + window.location.search,
    });
    if (!remembered && options.autoTopupAuthorization) {
      useToastStore.getState().actions.addToast('Auto top-up can be enabled in Credits settings after your purchase.', 'error');
    }
    setWhopCheckout({
      planId: response.planId,
      configurationId: response.configurationId,
      checkoutContextId: response.checkoutContextId,
      // Whop's hosted checkout for the same configuration. Kept as an escape hatch:
      // a bank challenge that cannot finish inside the embed usually completes
      // at the top level.
      hostedCheckoutUrl: response.checkoutUrl || null,
      type,
      creditPackId: type === 'credits' ? options.creditPackId : undefined,
      fallbackFromValmar,
      purpose,
      promoHint: purpose === 'update-payment' ? '' : promoHint,
      options,
    });
    setIsOpen(true);
    // Updating a card is not a purchase funnel entry.
    if (purpose !== 'update-payment') {
      checkoutTracker.start({ processor: 'whop', checkoutType: type === 'credits' ? 'credits' : 'plus' });
    }
  }, [checkoutTracker]);

  const openWhopPlusCheckout = useCallback(async (options = {}, { fallbackFromValmar = false, keepPendingPromise = false, purpose = null } = {}) => {
    const generation = ++whopRequestGeneration.current;
    assertClientCanStartPaidCheckout(userHeldForReview);
    const rawPlan = options.plusPlanId || 'monthly';
    const planLength = (
      rawPlan === 'yearly' ||
      rawPlan === 'monthly' ||
      rawPlan === 'premium-yearly' ||
      rawPlan === 'premium-monthly'
    ) ? rawPlan : 'monthly';
    // Any web checkout by an existing Plus subscriber is an extension or tier
    // change. Preserve their remaining paid time even when the caller came
    // from a generic pricing CTA that did not explicitly set mode=extend.
    const isExtend = options.mode === 'extend' || userHasValidPlus;
    console.log('[GabPurchase] Opening Whop embedded checkout for Plus, plan:', planLength, 'isExtend:', isExtend, 'fallbackFromValmar:', fallbackFromValmar);

    const response = await whopApi.createPlusCheckout({
      planLength,
      source: checkoutSource,
      isExtend,
      allowPromoCodes: purpose !== 'update-payment',
      checkoutPurpose: purpose === 'update-payment' ? 'update-payment' : 'plus',
    });
    console.log('[GabPurchase] Whop API response:', response);

    if (generation !== whopRequestGeneration.current) throw new Error('Checkout closed');

    if (!response?.planId || !response?.configurationId || !response?.checkoutContextId) {
      throw new Error('No checkout configuration returned from Whop API');
    }

    launchWhopCheckoutUi(
      response,
      { ...options, plusPlanId: planLength, isExtend },
      { fallbackFromValmar, type: 'plus', purpose }
    );
    return keepPendingPromise ? undefined : beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, checkoutSource, launchWhopCheckoutUi, userHasValidPlus, userHeldForReview]);

  const openWhopCreditsCheckout = useCallback(async (options = {}, { fallbackFromValmar = false, keepPendingPromise = false } = {}) => {
    const generation = ++whopRequestGeneration.current;
    assertClientCanStartPaidCheckout(userHeldForReview);
    console.log('[GabPurchase] Opening Whop embedded credits checkout', 'fallbackFromValmar:', fallbackFromValmar);

    const response = await whopApi.createCreditsCheckout({
      creditPackId: options.creditPackId,
      source: checkoutSource,
    });
    console.log('[GabPurchase] Whop API response:', response);

    if (generation !== whopRequestGeneration.current) throw new Error('Checkout closed');

    if (!response?.planId || !response?.configurationId || !response?.checkoutContextId) {
      throw new Error('No checkout configuration returned from Whop API');
    }

    launchWhopCheckoutUi(response, options, { fallbackFromValmar, type: 'credits' });
    return keepPendingPromise ? undefined : beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, checkoutSource, launchWhopCheckoutUi, userHeldForReview]);

  useEffect(() => {
    openWhopPlusCheckoutRef.current = openWhopPlusCheckout;
    openWhopCreditsCheckoutRef.current = openWhopCreditsCheckout;
  }, [openWhopPlusCheckout, openWhopCreditsCheckout]);

  // Elements handles declines/retries in its own UI. Host recovery is for
  // load failures and unsuccessful returns from external payment steps.
  const handleWhopPaymentError = useCallback((error) => {
    const message = describeWhopPaymentError(error);
    console.warn('[GabPurchase] Whop payment error:', message, error);
    setWhopPaymentError(message);
    // A load failure does not withdraw the customer's explicit choice. Hosted
    // recovery uses this same order and returns in the same tab.
    checkoutTracker.fail(message);

    // A failed attempt may have tripped a payment-failure review hold on the
    // backend (3 declines / 24h). The hold lands asynchronously via webhook,
    // so refetch now — otherwise the stale client flag lets the user retry
    // into a checkout whose charge would be silently withheld.
    try {
      useAuthStore.getState().fetchUserSlim?.().then?.(() => {
        if (useAuthStore.getState().user?.flags?.heldForReview === true) {
          useModalStore.getState().actions.openModal('AccountReviewHoldModal', { source: 'payment_error' });
        }
      }).catch?.(() => {});
    } catch (_) { /* best effort */ }

    if (typeof pendingPromiseRef.current.onEvent === 'function') {
      try {
        pendingPromiseRef.current.onEvent({ name: 'whopPaymentError', data: { message } });
      } catch (_) {}
    }
  }, [checkoutTracker]);

  // Recover a load failure with a fresh configuration and element.
  const retryWhopCheckout = useCallback(async () => {
    const current = whopCheckout;
    if (!current || whopRetrying) return;
    setWhopRetrying(true);
    const generation = whopRequestGeneration.current + 1;
    // Keep the failed form unmounted until a replacement configuration arrives.
    const options = { ...current.options,
      ...(whopAutoOffer ? { autoTopupAuthorization: whopAutoChecked ? whopAutoOffer.settings : null } : {}),
    };

    try {
      if (current.type === 'credits') {
        await openWhopCreditsCheckoutRef.current?.(options, {
          fallbackFromValmar: current.fallbackFromValmar,
          keepPendingPromise: true,
        });
      } else {
        await openWhopPlusCheckoutRef.current?.(options, {
          fallbackFromValmar: current.fallbackFromValmar,
          keepPendingPromise: true,
          purpose: current.purpose,
        });
      }
    } catch (error) {
      if (generation !== whopRequestGeneration.current) return;
      console.error('[GabPurchase] Failed to restart Whop checkout:', error);
      setWhopPaymentError(describeWhopPaymentError(error));
    } finally {
      if (generation === whopRequestGeneration.current) setWhopRetrying(false);
    }
  }, [whopCheckout, whopRetrying, whopAutoChecked, whopAutoOffer]);

  const whopStatusWatchAliveRef = useRef(true);
  const whopFollowUpTimersRef = useRef([]);
  // Declared before the checkout effect so that, on unmount, this cleanup runs
  // after that effect has scheduled its last checks and can cancel them.
  useEffect(() => () => {
    whopStatusWatchAliveRef.current = false;
    whopFollowUpTimersRef.current.forEach(clearTimeout);
    whopFollowUpTimersRef.current = [];
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || !whopCheckout || whopPaymentError) return;
    let disposed = false;
    let ready = false;
    let checkout;
    let readyTimer;
    let statusTimer;
    const { checkoutContextId } = whopCheckout;
    const returnUrl = whopReturnUrl;
    // Elements redirects the tab to returnUrl after payment and fulfillment is
    // webhook-driven, so a blocked redirect, a backdrop tap while the charge
    // settles, or a form that crashes after charging would hide a paid order
    // and invite a second one. A fulfilled receipt for this order opens the
    // return page itself; anything short of that changes nothing here.
    const followOrder = async () => {
      if (!whopStatusWatchAliveRef.current) return false;
      let status = null;
      try { status = (await whopApi.getCheckoutStatus(checkoutContextId))?.status ?? null; } catch (_) { status = null; }
      if (!whopStatusWatchAliveRef.current) return false;
      // Elements shows a decline only to the buyer, so the failed order context
      // recorded from payment.failed is the funnel's one decline signal. The
      // tracker resolves an attempt once; later polls and the close are no-ops.
      if (status === 'failed' && !disposed) checkoutTracker.fail('processor_declined');
      if (status !== 'completed') return false;
      openWhopReturnPage(returnUrl);
      return true;
    };
    const watchStatus = () => {
      statusTimer = setTimeout(async () => {
        if (await followOrder()) return;
        if (!disposed && whopStatusWatchAliveRef.current) watchStatus();
      }, WHOP_STATUS_POLL_MS);
    };
    setWhopLoading(true);
    loadWhopElements().then((WhopElements) => {
      if (disposed) return;
      // Without a mount point the form can never report ready; do not leave
      // the buyer on the loading state with no way out.
      if (!whopContainerRef.current) {
        handleWhopPaymentError('The secure payment form could not be displayed. Please use secure checkout below or try again.');
        return;
      }
      readyTimer = setTimeout(() => {
        if (!disposed) handleWhopPaymentError('The secure payment form is taking too long to load. Please use secure checkout below or try again.');
      }, 25000);
      checkout = WhopElements().checkout.create({
        checkoutConfiguration: whopCheckout.configurationId,
        returnUrl,
        ...(whopCheckout.promoHint ? { promoCode: whopCheckout.promoHint } : {}),
        appearance: whopAppearanceRef.current,
      });
      whopElementRef.current = checkout;
      checkout.create('checkout', {
        buyerEmail: userEmail || '',
        onReady: () => {
          clearTimeout(readyTimer);
          if (disposed || ready) return;
          ready = true;
          setWhopLoading(false);
          watchStatus();
        },
        onError: (error) => { if (!disposed) handleWhopPaymentError(error); },
      }).mount(whopContainerRef.current);
    }).catch((error) => { if (!disposed) handleWhopPaymentError(error); });
    return () => {
      disposed = true;
      clearTimeout(readyTimer);
      clearTimeout(statusTimer);
      if (ready && whopStatusWatchAliveRef.current) {
        WHOP_STATUS_AFTER_CLOSE_MS.forEach(delay => {
          const timer = setTimeout(() => { followOrder(); }, delay);
          whopFollowUpTimersRef.current.push(timer);
        });
      }
      checkout?.destroy();
      if (whopElementRef.current === checkout) whopElementRef.current = null;
    };
    // Order options are immutable. Appearance changes update the mounted form
    // below, preserving entered card details when the app theme changes.
  }, [whopCheckout, whopReturnUrl, whopPaymentError, userEmail, handleWhopPaymentError, checkoutTracker]);

  useEffect(() => {
    whopElementRef.current?.update({ appearance: whopAppearanceRef.current });
  }, [isDarkTheme, colors]);

  const closeCheckout = useCallback((reason) => {
    whopRequestGeneration.current += 1;
    setWhopRetrying(false);
    saveWhopCheckoutOptIn({ authorization: null });
    setIsOpen(false);
    setIsIframeLoaded(false);
    setCheckoutUrl(null);
    setWhopCheckout(null);
    setWhopPaymentError(null);
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    // No-op unless a purchase attempt is actually open, so closing an
    // update-payment iframe is not counted as an abandoned checkout.
    checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
    if (pendingPromiseRef.current.reject) {
      pendingPromiseRef.current.reject(reason instanceof Error ? reason : new Error(reason || 'Checkout closed'));
    }
    pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
  }, [checkoutTracker]);

  const closeCreditPacksCheckout = useCallback((reason) => {
    whopRequestGeneration.current += 1;
    setWhopRetrying(false);
    saveWhopCheckoutOptIn({ authorization: null });
    setIsOpen(false);
    setIsIframeLoaded(false);
    setCheckoutUrl(null);
    setWhopCheckout(null);
    setWhopPaymentError(null);
    pendingValmarCheckoutRef.current = null;
    pendingValmarUpdateRef.current = null;
    checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.CLOSED);
    if (pendingPromiseRef.current.reject) {
      pendingPromiseRef.current.reject(reason instanceof Error ? reason : new Error(reason || 'Checkout closed'));
    }
    pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
  }, [checkoutTracker]);

  const fallbackToWhopCheckout = useCallback(async (pendingCheckout) => {
    if (!pendingCheckout) return false;

    clearValmarAvailabilityCache();
    pendingValmarCheckoutRef.current = null;
    setCheckoutUrl(null);
    setIsIframeLoaded(false);

    try {
      if (pendingCheckout.type === 'plus') {
        await openWhopPlusCheckoutRef.current?.(pendingCheckout.options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
      } else if (pendingCheckout.type === 'credits') {
        await openWhopCreditsCheckoutRef.current?.(pendingCheckout.options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
      } else {
        return false;
      }
      return true;
    } catch (error) {
      console.error('[GabPurchase] Failed to fall back to Whop checkout:', error);
      const reject = pendingPromiseRef.current.reject;
      pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
      if (reject) {
        reject(error instanceof Error ? error : new Error('Unable to open alternate checkout'));
      }
      setIsOpen(false);
      setWhopCheckout(null);
      return false;
    }
  }, []);


  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleMessage = (event) => {
      if (!checkoutIframeRef.current || !event.source || event.source !== checkoutIframeRef.current.contentWindow) {
        return;
      }
      if (checkoutUrl) {
        try {
          const expectedOrigin = new URL(checkoutUrl, window.location.href).origin;
          if (event.origin && event.origin !== expectedOrigin) return;
        } catch (_) {}
      }
      const data = event?.data;
      if (!data || (data.source !== 'checkout-iframe' && data.source !== 'credit-checkout-iframe' && data.source !== 'update-payment-iframe')) return;

      const action = data.action;
      const payload = data.data || {};

      if (typeof pendingPromiseRef.current.onEvent === 'function') {
        try { pendingPromiseRef.current.onEvent({ name: action, data: payload }); } catch (_) {}
      }

      switch (action) {
        case 'closeCheckoutIframe':
          closeCheckout('User closed checkout');
          break;
        case 'checkoutStarted':
        case 'creditCheckoutValidated':
        case 'checkoutValidated':
        case 'checkoutTokenizationStarted':
        case 'checkoutTokenizationSuccess':
        case 'updatePaymentStarted':
        case 'updatePaymentValidated':
        case 'updatePaymentTokenizationStarted':
        case 'updatePaymentTokenizationSuccess':
          break;
        case 'updatePaymentSuccess': {
          const resolve = pendingPromiseRef.current.resolve;
          // Close the checkout UI
          setIsOpen(false);
          setIsIframeLoaded(false);
          setCheckoutUrl(null);
          // Resolve any awaiting promise
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          if (resolve) resolve({ success: true, ...payload });
          // Inform user
          try { window.alert('Payment method updated successfully!'); } catch (_) {}
          try { window.location.reload(); } catch (_) {}
          break;
        }
        case 'updatePaymentCancelled':
        case 'updatePaymentClose': {
          closeCheckout('User closed update payment');
          break;
        }
        case 'creditCheckoutSuccess':
        case 'checkoutSuccess': {
          const attempt = checkoutTracker.peek();
          checkoutTracker.complete();
          trackPurchaseCompleted({
            checkoutType: attempt?.checkoutType || (action === 'creditCheckoutSuccess' ? 'credits' : 'plus'),
            processor: attempt?.processor || 'valmar',
          });
          const resolve = pendingPromiseRef.current.resolve;
          // Close the checkout UI
          setIsOpen(false);
          setIsIframeLoaded(false);
          setCheckoutUrl(null);
          // Resolve any awaiting promise
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          if (resolve) resolve({ success: true, ...payload });
          trackWhopPurchase({
            id: payload.purchaseId || payload.receiptId || payload.creditPackId,
          });
          // Inform user and refresh the page
          try { window.alert('Thank you! Your purchase was successful.'); } catch (_) {}
          try { window.location.reload(); } catch (_) {}
          break;
        }
        case 'checkoutError':
        case 'checkoutNetworkError':
        case 'creditCheckoutError':
        case 'creditCheckoutNetworkError': {
          // Declines and gateway errors. A no-op when nothing is open.
          checkoutTracker.fail(payload?.errorMessage);
          break;
        }
        case 'updatePaymentError':
        case 'updatePaymentNetworkError': {
          break;
        }
        case 'updatePaymentTokenizationError': {
          const pendingUpdate = pendingValmarUpdateRef.current;
          if (!pendingUpdate || !isValmarBlockedError(payload)) {
            break;
          }

          console.warn('[GabPurchase] Valmar payment update blocked in iframe, falling back to Whop:', payload?.errorMessage);
          clearValmarAvailabilityCache();
          pendingValmarUpdateRef.current = null;
          setCheckoutUrl(null);
          setIsIframeLoaded(false);

          const updateError = new Error('The card update form is unavailable. Allow the secure payment form or contact support. No new subscription was started.');
          const reject = pendingPromiseRef.current.reject;
          pendingPromiseRef.current = { resolve: null, reject: null, onEvent: null };
          reject?.(updateError);
          useToastStore.getState().actions.addToast(updateError.message, 'error');
          setIsOpen(false);
          setWhopCheckout(null);
          break;
        }
        case 'checkoutTokenizationError':
        case 'creditCheckoutTokenizationError': {
          const pendingCheckout = pendingValmarCheckoutRef.current;
          if (!pendingCheckout || !isValmarBlockedError(payload)) {
            // No fallback is coming, so the card itself was rejected at
            // tokenization and this attempt is over. Without this the buyer
            // closes the modal instead and the decline is filed as
            // abandonment.
            checkoutTracker.fail(payload?.errorMessage || 'tokenization_error');
            break;
          }

          console.warn('[GabPurchase] Valmar checkout blocked in iframe, falling back to Whop:', payload?.errorMessage);
          fallbackToWhopCheckout({
            ...pendingCheckout,
            options: {
              ...pendingCheckout.options,
              promoCode: payload?.promoCode || pendingCheckout.options?.promoCode,
            },
          });
          break;
        }
        default:
          break;
      }
    };

    if (Platform.OS !== 'web') return;
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkoutTracker, closeCheckout, fallbackToWhopCheckout, checkoutUrl]);

  // Leaving the page mid-payment is the one abandonment no UI callback can
  // report: nothing is closed, nothing fails, the attempt just stops existing.
  // `pagehide` covers tab close, reload and navigation, where `beforeunload` is
  // unreliable on mobile Safari. A no-op when no attempt is open.
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePageHide = (event) => {
      // `persisted` means the page is going into the back/forward cache and may
      // come back with the checkout still open, so it is not an abandonment.
      // Elements navigates the tab for success and bank authentication too.
      // A navigation alone cannot be classified as an abandoned Whop purchase.
      if (event?.persisted || whopCheckout) return;
      checkoutTracker.abandon(CHECKOUT_ABANDON_REASON.NAVIGATED_AWAY);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [checkoutTracker, whopCheckout]);

  const buildCheckoutUrl = useCallback(({
    sandbox = false, // !!! TESTING !!!
    plusPlanId,
    creditPackId,
    promoCode,
    theme: themeOverride,
    mode,
  } = {}) => {
    const baseUrl = API_HOST;
    const path = creditPackId ? '/checkout/credit-payment' : '/checkout/payment';
    const endpoint = `${baseUrl}${path}`;
    const params = new URLSearchParams();
    
    if (userId) params.set('userId', String(userId));
    if (userEmail) params.set('email', String(userEmail));

    const effectiveTheme = themeOverride || (isDarkTheme ? 'dark' : 'light');
    if (effectiveTheme) params.set('theme', effectiveTheme);

    if (
      plusPlanId === 'monthly' ||
      plusPlanId === 'yearly' ||
      plusPlanId === 'premium-monthly' ||
      plusPlanId === 'premium-yearly'
    ) {
      params.set('planId', plusPlanId);
    }
    if (creditPackId) params.set('creditPackId', creditPackId);
    if (promoCode) params.set('promoCode', String(promoCode));
    if (!creditPackId && (mode === 'extend' || userHasValidPlus)) {
      params.set('mode', 'extend');
    }

    if (sandbox) params.set('sandbox', '1');

    return `${endpoint}?${params.toString()}`;
  }, [userEmail, userId, isDarkTheme, userHasValidPlus]);


  const openCheckout = useCallback(async (options = {}) => {
    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to purchase a subscription'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    // Plus Premium is web-only — never route through native IAP.
    const isPremiumPlan = typeof options.plusPlanId === 'string'
      && options.plusPlanId.startsWith('premium-');
    if (isPremiumPlan && Platform.OS !== 'web') {
      return Promise.reject(new Error('Plus Premium is only available on the web.'));
    }

    // iOS/Android: use IAP for subscription purchases when store is connected
    if (Platform.OS !== 'web' && options.plusPlanId) {
      const subscribedStore = activePurchase?.processor;
      const deviceStore = Platform.OS === 'android' ? PROCESSOR.GOOGLE : PROCESSOR.APPLE;
      if (activePurchase?.status === 'active' && [PROCESSOR.APPLE, PROCESSOR.GOOGLE].includes(subscribedStore)
        && subscribedStore !== deviceStore) {
        throw new Error(`Manage this subscription in ${subscribedStore === PROCESSOR.GOOGLE ? 'Google Play' : 'the App Store'} to avoid a second subscription.`);
      }
      let storeConnected = !!iap.isConnected;
      const productForCadence = nativeSubscriptionPlans(iap.products, Platform.OS)
        .find(plan => plan.cadence === options.plusPlanId);
      const subscriptionCandidates = [productForCadence?.productId
        || iap.PRODUCT_IDS?.[String(options.plusPlanId).toUpperCase()]].filter(Boolean);
      let iapProduct = null;
      let selectedProductId = null;

      for (const candidate of subscriptionCandidates) {
        const product = iap.getProduct(candidate);
        if (product) {
          iapProduct = product;
          selectedProductId = candidate;
          break;
        }
      }

      console.log('[GabPurchase] IAP check:', {
        productCandidates: subscriptionCandidates,
        selectedProductId,
        isConnected: storeConnected,
        hasProduct: !!iapProduct,
        totalProducts: iap.products?.length,
        totalCreditPacks: iap.creditPackProducts?.length,
      });

      // If connected but products not loaded yet, try fetching them now
      if (subscriptionCandidates.length > 0 && storeConnected && !iapProduct) {
        console.log('[GabPurchase] Products not loaded, retrying fetchProducts...');
        try {
          const fetched = await iap.fetchProducts();
          const fetchedSubscriptions = fetched?.subscriptions || [];
          for (const candidate of subscriptionCandidates) {
            const product = fetchedSubscriptions.find((p) => p?.productId === candidate);
            if (product) {
              iapProduct = product;
              selectedProductId = candidate;
              break;
            }
          }
          console.log('[GabPurchase] After refetch, hasProduct:', !!iapProduct, 'selectedProductId:', selectedProductId);
        } catch (fetchErr) {
          console.error('[GabPurchase] fetchProducts retry failed:', fetchErr);
        }
      }

      // If not connected, try connecting first
      if (subscriptionCandidates.length > 0 && !storeConnected) {
        console.log('[GabPurchase] Store not connected, attempting connect...');
        try {
          const connected = await iap.connect();
          storeConnected = !!connected;
          const fetched = await iap.fetchProducts();
          const fetchedSubscriptions = fetched?.subscriptions || [];
          for (const candidate of subscriptionCandidates) {
            const product = fetchedSubscriptions.find((p) => p?.productId === candidate);
            if (product) {
              iapProduct = product;
              selectedProductId = candidate;
              break;
            }
          }
          console.log('[GabPurchase] After connect+fetch, hasProduct:', !!iapProduct, 'selectedProductId:', selectedProductId);
        } catch (connectErr) {
          console.error('[GabPurchase] Connect retry failed:', connectErr);
        }
      }

      if (selectedProductId && storeConnected && iapProduct) {
        console.log('[GabPurchase] Using IAP for subscription purchase:', selectedProductId);
        checkoutTracker.start({ processor: nativeStoreProcessor(), checkoutType: 'plus' });
        if (!nativeSubscriptionPlans([iapProduct], Platform.OS).some(plan => plan.cadence === options.plusPlanId)) {
          throw new Error('This store product does not match the selected billing period. Please reload the plans.');
        }
        const result = await iap.purchaseProduct(selectedProductId, iapProduct);
        trackNativeIapOutcome(result);
        return nativeCheckoutResult(result);
      }
      // iOS/Android/macOS: IAP is required — no external payment fallback
      if (isNativePlatform) {
        const storeName = (Platform.OS === 'ios' || Platform.OS === 'macos') ? 'App Store' : 'Google Play';
        const reason = !storeConnected
          ? `${storeName} connection failed`
          : !iapProduct
            ? `Products [${subscriptionCandidates.join(', ')}] not found in ${storeName} (${iap.products?.length || 0} loaded)`
            : 'Unknown error';
        console.error(`[GabPurchase] IAP not available on ${Platform.OS}:`, reason);
        throw new Error(`In-App Purchase is not available: ${reason}. Please restart the app and try again.`);
      }
      console.log('[GabPurchase] IAP not available for subscription, falling back to web checkout');
    }

    // Native (iOS/Android/macOS): block any non-IAP purchase path
    if (isNativePlatform) {
      console.error(`[GabPurchase] ${Platform.OS} requires IAP for all purchases`);
      throw new Error('In-App Purchase is not available right now. Please restart the app and try again.');
    }

    if (options.forceWhop) {
      clearValmarAvailabilityCache();
      pendingValmarCheckoutRef.current = { type: 'plus', options };
      try {
        await openWhopPlusCheckout(options, {
          fallbackFromValmar: true,
          keepPendingPromise: true,
        });
        return beginPendingCheckoutPromise(options);
      } catch (error) {
        console.error('[GabPurchase] Forced Whop checkout error:', error);
        throw error;
      }
    }

    // Check if user is international or Valmar is blocked (e.g. ad/script blockers)
    const { provider, userIsInternational, valmarAvailable } = await resolveCheckoutProvider();

    if (provider === 'whop') {
      try {
        return await openWhopPlusCheckout(options, {
          fallbackFromValmar: !userIsInternational && !valmarAvailable,
        });
      } catch (error) {
        console.error('[GabPurchase] Whop checkout error:', error);
        throw error;
      }
    }

    // US users with Valmar available continue with Valmar checkout
    pendingValmarCheckoutRef.current = { type: 'plus', options };
    const url = await accountApi.authorizeCheckoutUrl(
      buildCheckoutUrl(options), options.creditPackId ? 'credits' : 'plus',
    );
    setWhopCheckout(null);
    setCheckoutUrl(url);
    setIsIframeLoaded(false);
    setIsOpen(true);
    checkoutTracker.start({ processor: 'valmar', checkoutType: 'plus' });

    return beginPendingCheckoutPromise(options);
  }, [buildCheckoutUrl, beginPendingCheckoutPromise, checkoutTracker, trackNativeIapOutcome, openWhopPlusCheckout, resolveCheckoutProvider, iap, userHeldForReview, activePurchase, userId, userEmail]);

  const openCreditPacksCheckout = useCallback(async (options = {}) => {
    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to purchase credits'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    // Native (iOS/Android): use IAP for credit packs when product is available
    if (Platform.OS !== 'web') {
      let storeConnected = !!iap.isConnected;
      // Resolve credits from options — may be undefined if caller only passed creditPackId
      let credits = options.credits;
      if (credits == null && options.creditPackId) {
        // Try to extract credits from creditPackId (e.g. "credit-pack-700" -> 700)
        const match = String(options.creditPackId).match(/(\d+)/);
        if (match) credits = Number(match[1]);
        console.log('[GabPurchase] Resolved credits from creditPackId:', options.creditPackId, '->', credits);
      }

      if (credits != null) {
        const iapProductId = getCreditPackIapProductId(credits);
        let hasProduct = iapProductId ? !!iap.getProduct(iapProductId) : false;

        console.log('[GabPurchase] Credit pack IAP check:', {
          credits,
          iapProductId,
          isConnected: storeConnected,
          hasProduct,
          totalCreditPacks: iap.creditPackProducts?.length,
        });

        // If connected but product not loaded, retry fetch
        if (iapProductId && storeConnected && !hasProduct) {
          console.log('[GabPurchase] Credit pack product not loaded, retrying fetchProducts...');
          try {
            const fetched = await iap.fetchProducts();
            const fetchedCreditPacks = fetched?.creditPacks || [];
            hasProduct = fetchedCreditPacks.some((p) => p?.productId === iapProductId);
            console.log('[GabPurchase] After refetch, hasProduct:', hasProduct);
          } catch (fetchErr) {
            console.error('[GabPurchase] fetchProducts retry failed:', fetchErr);
          }
        }

        // If not connected, try connecting first
        if (iapProductId && !storeConnected) {
          console.log('[GabPurchase] Store not connected for credit pack, attempting connect...');
          try {
            const connected = await iap.connect();
            storeConnected = !!connected;
            const fetched = await iap.fetchProducts();
            const fetchedCreditPacks = fetched?.creditPacks || [];
            hasProduct = fetchedCreditPacks.some((p) => p?.productId === iapProductId);
          } catch (connectErr) {
            console.error('[GabPurchase] Connect retry failed:', connectErr);
          }
        }

        if (iapProductId && storeConnected && hasProduct) {
          checkoutTracker.start({ processor: nativeStoreProcessor(), checkoutType: 'credits' });
          const result = await iap.purchaseCreditPack(credits);
          trackNativeIapOutcome(result);
          const outcome = nativeCheckoutResult(result);
          if (outcome.pending) useToastStore.getState().actions.addToast('Your purchase is pending verification. Use Restore; do not purchase again.', 'info');
          else if (!outcome.success && !outcome.cancelled) useToastStore.getState().actions.addToast(outcome.error || 'Purchase failed.', 'error');
          return outcome;
        }

        // iOS/Android/macOS: IAP is required — no external payment fallback
        if (isNativePlatform) {
          const storeName = (Platform.OS === 'ios' || Platform.OS === 'macos') ? 'App Store' : 'Google Play';
          const reason = !storeConnected
            ? `${storeName} connection failed`
            : !iapProductId
              ? `No IAP product for ${credits} credits`
              : `Product "${iapProductId}" not found (${iap.creditPackProducts?.length || 0} loaded)`
            ;
          console.error(`[GabPurchase] IAP not available for credit pack on ${Platform.OS}:`, reason);
          throw new Error(`Credit pack purchase not available: ${reason}. Please restart the app and try again.`);
        }
      } else if (isNativePlatform) {
        console.error(`[GabPurchase] Cannot determine credits amount for IAP on ${Platform.OS}`);
        throw new Error('Credit pack purchase failed: unable to determine credit amount. Please try again.');
      }
    }

    // Native (iOS/Android/macOS): block any non-IAP credit pack purchase path
    if (isNativePlatform) {
      console.error(`[GabPurchase] ${Platform.OS} requires IAP for all credit pack purchases`);
      throw new Error('In-App Purchase is not available right now. Please restart the app and try again.');
    }

    // Check if user is international or Valmar is blocked (e.g. ad/script blockers)
    const { provider, userIsInternational, valmarAvailable } = await resolveCheckoutProvider();

    if (provider === 'whop') {
      try {
        return await openWhopCreditsCheckout(options, {
          fallbackFromValmar: !userIsInternational && !valmarAvailable,
        });
      } catch (error) {
        console.error('[GabPurchase] Whop checkout error:', error);
        throw error;
      }
    }

    // US users with Valmar available continue with Valmar checkout
    pendingValmarCheckoutRef.current = { type: 'credits', options };
    const url = await accountApi.authorizeCheckoutUrl(
      buildCheckoutUrl(options), options.creditPackId ? 'credits' : 'plus',
    );
    setWhopCheckout(null);
    setCheckoutUrl(url);
    setIsIframeLoaded(false);
    setIsOpen(true);
    checkoutTracker.start({ processor: 'valmar', checkoutType: 'credits' });

    return beginPendingCheckoutPromise(options);
  }, [buildCheckoutUrl, beginPendingCheckoutPromise, checkoutTracker, trackNativeIapOutcome, openWhopCreditsCheckout, resolveCheckoutProvider, iap, userHeldForReview]);

  const openUpdatePaymentMethod = useCallback(async (updateUrl, options = {}) => {
    if (Platform.OS !== 'web') {
      return Promise.reject(new Error('Payment method update is only available on web'));
    }
    assertClientCanStartPaidCheckout(userHeldForReview);

    if (!userId || !userEmail) {
      return Promise.reject(new Error('User must be logged in to update payment method'));
    }

    if (!updateUrl) {
      return Promise.reject(new Error('Update URL is required'));
    }

    const supportedPlanLengths = ['monthly', 'yearly', 'premium-monthly', 'premium-yearly'];
    const planLength = supportedPlanLengths.includes(options.planLength)
      ? options.planLength
      : 'monthly';
    const valmarAvailable = await checkValmarAvailable();

    if (!valmarAvailable) {
      console.log('[GabPurchase] Valmar blocked for payment update, using Whop migration checkout');
      return openWhopPlusCheckout(
        { plusPlanId: planLength, mode: 'extend' },
        { fallbackFromValmar: true, purpose: 'update-payment' },
      );
    }

    pendingValmarUpdateRef.current = { updateUrl, options, planLength };
    setWhopCheckout(null);
    setCheckoutUrl(await accountApi.authorizeCheckoutUrl(updateUrl, 'update-payment'));
    setIsIframeLoaded(false);
    setIsOpen(true);

    return beginPendingCheckoutPromise(options);
  }, [beginPendingCheckoutPromise, openWhopPlusCheckout, userEmail, userId, userHeldForReview]);

  const contextValue = useMemo(() => ({
    openCheckout,
    closeCheckout,
    openCreditPacksCheckout,
    closeCreditPacksCheckout,
    openUpdatePaymentMethod,
    isOpen,
    isInternational,
    usesWhopCheckout: isInternational || isWhopSubscriber,
    iap, // Single IAP connection for subscriptions + credit packs (iOS/Android)
  }), [openCheckout, closeCheckout, openCreditPacksCheckout, closeCreditPacksCheckout, openUpdatePaymentMethod, isOpen, isInternational, isWhopSubscriber, iap]);

  return (
    <GabPurchaseContext.Provider value={contextValue}>
      {children}
      {/* Checkout modal - shows either Valmar iframe (US) or Whop embed (international) */}
      {Platform.OS === 'web' && isOpen && (
        <View style={{ position: 'fixed', zIndex: 9999, top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <Pressable onPress={() => closeCheckout('Backdrop close')} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1 }} />

          {/* Close button on backdrop (outside iframe container) */}
          <Pressable
            onPress={() => closeCheckout('User closed with button')}
            accessibilityRole="button"
            accessibilityLabel="Close checkout"
            style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, alignItems: 'center', justifyContent: 'center', zIndex: 3 }}
          >
            <Icon name="xmark" size={18} color='#ffffff' solid />
          </Pressable>

          {/* Scrollable container for Whop checkout */}
          {whopCheckout ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: isXS ? 50 : 40,
                paddingBottom: 40,
                paddingLeft: isXS ? 10 : 40,
                paddingRight: isXS ? 10 : 40,
              }}
            >
              {/* Whop Elements checkout */}
              {whopCheckout.fallbackFromValmar && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    marginBottom: 12,
                    padding: '10px 14px',
                    borderRadius: 8,
                    backgroundColor: colors.solid_color_block,
                    color: colors.text_color_secondary,
                    fontSize: 13,
                    lineHeight: '18px',
                  }}
                >
                  {whopCheckout.purpose === 'update-payment'
                    ? 'Your browser blocked our secure card form. Update your payment through alternate checkout below. Your remaining subscription time will be preserved.'
                    : 'Your browser blocked our secure card form. We switched you to alternate checkout so you can still complete payment.'}
                </div>
              )}
              {whopAutoOffer && !whopPaymentError && <View style={{ width: '100%', maxWidth: 500, marginBottom: 14 }}>
                <AutoTopupCheckoutOptIn pack={whopAutoOffer.pack} checked={whopAutoChecked} onChange={checked => {
                  const authorization = checked ? whopAutoOffer.settings : null;
                  const saved = saveWhopCheckoutOptIn({ contextId: whopCheckout.checkoutContextId, userId, authorization });
                  if (!saved) {
                    useToastStore.getState().actions.addToast('Could not save your auto top-up preference. Please try again.', 'error');
                    return;
                  }
                  setWhopAutoChecked(checked);
                }} />
              </View>}
              {/* Elements owns payment declines; this fallback handles load failures. */}
              {whopPaymentError ? (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    padding: '16px 18px',
                    borderRadius: 12,
                    backgroundColor: colors.solid_color_block,
                    color: colors.text_color_primary,
                    fontSize: 14,
                    lineHeight: '20px',
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: '600' }}>{whopPaymentError}</div>
                  <div style={{ color: colors.text_color_secondary, fontSize: 13, lineHeight: '18px', marginBottom: 14 }}>
                    {whopCheckout.hostedCheckoutUrl
                      ? 'Continue on our secure checkout page. We’ll bring you back here when you’re done.'
                      : 'You can start a new payment attempt below.'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {!!whopCheckout.hostedCheckoutUrl && (
                      <a
                        href={whopCheckout.hostedCheckoutUrl}
                        rel="noopener noreferrer"
                        style={{
                          padding: '12px 16px',
                          borderRadius: 8,
                          background: isDarkTheme ? '#1F6B47' : '#123524',
                          color: '#ffffff',
                          fontSize: 15,
                          fontWeight: '600',
                          textAlign: 'center',
                          textDecoration: 'none',
                        }}
                      >
                        Open secure checkout
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={retryWhopCheckout}
                      disabled={whopRetrying}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        background: 'transparent',
                        color: colors.text_color_secondary,
                        border: `1px solid ${colors.border_color_secondary}`,
                        borderRadius: 8,
                        fontSize: 14,
                      }}
                    >
                      {whopRetrying ? 'Preparing checkout…' : whopCheckout.hostedCheckoutUrl ? 'Use the form here instead' : 'Try again'}
                    </button>
                  </div>
                </div>
              ) : (
              <div style={{ width: '100%', maxWidth: 500 }} aria-busy={whopLoading}>
                {whopLoading && <div role="status" style={{ padding: '12px 0', color: colors.text_color_secondary }}>Preparing your secure checkout…</div>}
                <div
                  ref={whopContainerRef}
                  style={{ width: '100%', minHeight: 400, backgroundColor: colors.solid_color_primary, borderRadius: 12 }}
                />
              </div>
              )}
            </div>
          ) : (
            <View
              style={{
                position: 'absolute',
                top: isXS ? 100 : 0,
                right: 0,
                bottom: 0,
                left: 0,
                alignItems: 'center',
                justifyContent: isXS ? 'flex-start' : 'center',
                paddingHorizontal: isXS ? 0 : 48,
                paddingVertical: isXS ? 0 : 24,
                zIndex: 2,
              }}
            >
              <View style={{ width: '100%', maxWidth: 1040, height: '100%', position: 'relative', backgroundColor: colors.solid_color_primary, borderRadius: 12, overflow: 'hidden' }}>
                {/* Valmar iframe checkout for US users */}
                {!isIframeLoaded && (
                  <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_primary }}>
                    <ActivityIndicator size="large" color={colors.text_color_primary} />
                  </View>
                )}

                {/* eslint-disable-next-line react/no-unknown-property */}
                <iframe
                  ref={checkoutIframeRef}
                  title="Gab Checkout"
                  src={checkoutUrl || ''}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  onLoad={() => setIsIframeLoaded(true)}
                />
              </View>
            </View>
          )}
        </View>
      )}

    </GabPurchaseContext.Provider>
  );
}

export function useGabPurchase() {
  const context = useContext(GabPurchaseContext);
  if (context === null) {
    throw new Error('useGabPurchase must be used within a GabPurchaseProvider');
  }
  return context;
}
